import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Group, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconBell, IconCheck, IconInfoCircle, IconX } from '@tabler/icons-react';
import { useReadNotification } from '@/hooks/useNotifications';
import { formatNotificationDate, sanitizeNotificationBody, statusColor } from '@/lib/notificationView';
import { navTargetFor } from '@/realtime/notificationCatalog';
import type { MessageStatus, NotificationItem as Item } from '@/types/notifications';
import classes from './NotificationItem.module.css';

const STATUS_ICON: Readonly<Record<Exclude<MessageStatus, 'EMPTY'>, typeof IconCheck>> = {
  SUCCESS: IconCheck,
  ERROR: IconX,
  NOTIFY: IconInfoCircle,
};

interface NotificationItemProps {
  readonly item: Item;
  /** Закрыть панель после перехода по уведомлению. */
  readonly onClose: () => void;
}

// Один элемент колокольчика: иконка/цвет по статусу, заголовок, тело (sanitized HTML), дата,
// точка непрочитанного. Клик → отметить прочитанным + (если есть таргет) перейти и закрыть панель.
export default function NotificationItem({ item, onClose }: NotificationItemProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const read = useReadNotification();

  const status = item.payload?.messageStatus;
  const Icon = status && status !== 'EMPTY' ? STATUS_ICON[status] : IconBell;
  const header = item.payload?.messageHeader;
  const bodyHtml = sanitizeNotificationBody(item.payload?.messageBody);
  const date = formatNotificationDate(item.createdDate);
  const target = navTargetFor(item.type, item.payload);

  function handleClick(): void {
    if (!item.isRead) {
      read.mutate(item.id);
    }
    if (target) {
      navigate(target);
      onClose();
    }
  }

  return (
    <UnstyledButton
      onClick={handleClick}
      className={`${classes.row}${item.isRead ? '' : ` ${classes.unread}`}`}
    >
      <Group align="flex-start" wrap="nowrap" gap="sm">
        <ThemeIcon variant="light" color={statusColor(status)} size="md" radius="xl">
          <Icon size={16} />
        </ThemeIcon>

        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          {header ? (
            <Text fw={600} size="sm">
              {header}
            </Text>
          ) : null}
          {bodyHtml ? (
            <Text
              size="sm"
              c="gray.7"
              component="div"
              className={classes.body}
              // Безопасно: HTML очищен DOMPurify (sanitizeNotificationBody), узкий allow-list тегов.
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          ) : null}
          {date ? (
            <Text size="xs" c="gray.7">
              {date}
            </Text>
          ) : null}
        </Stack>

        {item.isRead ? null : (
          <Box
            component="span"
            role="img"
            aria-label={t('app.notifications.unread')}
            w={8}
            h={8}
            mt={6}
            bg="brand.6"
            style={{ borderRadius: '50%', flexShrink: 0 }}
          />
        )}
      </Group>
    </UnstyledButton>
  );
}
